/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemSalesTaxMaybe from './LineItemSalesTaxMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import LineItemCancellationRefundMaybe from './LineItemCancellationRefundMaybe';

import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

// Revive the plain-JSON line-item snapshot from settlement.cancellation.originalLineItems
// into the shape expected by the rest of the OrderBreakdown chain (Money instances for
// unitPrice/lineTotal). The snapshot is written by server/api/cancel-booking.js.
const reviveSnapshotLineItem = item => ({
  ...item,
  unitPrice:
    item.unitPrice && item.unitPrice.amount != null
      ? new Money(item.unitPrice.amount, item.unitPrice.currency)
      : item.unitPrice,
  lineTotal:
    item.lineTotal && item.lineTotal.amount != null
      ? new Money(item.lineTotal.amount, item.lineTotal.currency)
      : item.lineTotal,
});

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    timeZone,
    currency,
    marketplaceName,
    dateType: dateTypeProp,
    categoryLevel1: categoryLevel1Prop,
    orderData,
    intl,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';

  // Prefer the cancellation snapshot when present so the breakdown shows the
  // originally-charged amounts. The cancel transition mutates Sharetribe's
  // lineItems in place; the snapshot was written by server/api/cancel-booking.js
  // immediately before that mutation.
  const cancellation = transaction?.attributes?.metadata?.settlement?.cancellation || null;
  const hasCancellationSnapshot = Array.isArray(cancellation?.originalLineItems)
    && cancellation.originalLineItems.length > 0;
  // Detect "no capture occurred" terminal states: pre-acceptance customer cancel,
  // provider decline, expire, and operator-cancel-before-capture. These reach a
  // refunded transition without ever passing ACCEPTED, so payinTotal was never
  // captured. We use this to fall through to the native Sharetribe rendering
  // (LineItemTotalPrice + reversal lineItems) for the customer, and to zero
  // out provider-side subtotal/commission/refund rows.
  const lastTransition = transaction?.attributes?.lastTransition;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  let noCaptureOccurred = false;
  try {
    const proc = processName ? getProcess(processName) : null;
    const isRefundedTerminal = proc?.isRefunded?.(lastTransition) || false;
    const passedAccepted =
      proc?.states && proc?.hasPassedState
        ? proc.hasPassedState(proc.states.ACCEPTED, transaction)
        : false;
    noCaptureOccurred = isRefundedTerminal && !passedAccepted;
  } catch (e) {
    // process resolution can throw on inquiry/negotiation processes
  }
  const useSnapshotBranch = hasCancellationSnapshot && !noCaptureOccurred;
  const revivedOriginalLineItems = hasCancellationSnapshot
    ? cancellation.originalLineItems.map(reviveSnapshotLineItem)
    : null;
  const allLineItems = revivedOriginalLineItems || transaction.attributes.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  const dateType = dateTypeProp || ([LINE_ITEM_HOUR, LINE_ITEM_FIXED].includes(lineItemUnitType)
    ? DATE_TYPE_DATETIME
    : DATE_TYPE_DATE);

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  const categoryLevel1 = categoryLevel1Prop
    || transaction.listing?.attributes?.publicData?.categoryLevel1;
  const protectedData = transaction.attributes?.protectedData || {};
  const isRoundTrip = !!(protectedData.dockDeliveryRoundTrip || protectedData.isRoundTrip);
  const resolvedTimeZone = timeZone
    || transaction.listing?.attributes?.availabilityPlan?.timezone
    || 'America/Los_Angeles';

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={resolvedTimeZone}
        categoryLevel1={categoryLevel1}
        isRoundTrip={isRoundTrip}
        protectedData={protectedData}
        orderData={orderData}
      />

      <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} booking={booking} intl={intl} />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemSalesTaxMaybe lineItems={lineItems} intl={intl} publicData={transaction.listing?.attributes?.publicData} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
        noCaptureOccurred={noCaptureOccurred}
      />
      <LineItemRefundMaybe
        lineItems={lineItems}
        intl={intl}
        marketplaceCurrency={currency}
        noCaptureOccurred={noCaptureOccurred}
      />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
        noCaptureOccurred={noCaptureOccurred}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
        noCaptureOccurred={noCaptureOccurred}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
        noCaptureOccurred={noCaptureOccurred}
      />

      {useSnapshotBranch ? (
        <LineItemCancellationRefundMaybe
          transaction={transaction}
          originalLineItems={revivedOriginalLineItems}
          cancellation={cancellation}
          isProvider={isProvider}
          intl={intl}
          marketplaceCurrency={currency}
        />
      ) : (
        <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />
      )}

      {hasCommissionLineItem && !(noCaptureOccurred && isProvider) ? (
        <span className={css.feeInfo}>
          <FormattedMessage
            id={isProvider ? 'OrderBreakdown.commissionFeeNoteProvider' : 'OrderBreakdown.commissionFeeNote'}
          />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
